import classNames                                       from 'classnames';
import * as React                                       from 'react';
import { useTranslation }                               from 'react-i18next';
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import DV                                               from 'ts-data-validator';

import usePrevious from '../../../../common/hooks/usePrevious';

import RenderButton               from '../../../components/buttons/base/RenderButton';
import CompareButton              from '../../../components/buttons/CompareButton';
import CatalogMoreInfo            from '../../../components/catalog/CatalogMoreInfo';
import PopularSlider              from '../../../components/catalog/PopularSlider';
import ConsultationBlock          from '../../../components/consultationBlock';
import Preloader                  from '../../../components/containers/preloader';
import Dropdown                   from '../../../components/fields/dropdown';
import NextSliderArrow            from '../../../components/icons/NextSliderArrow';
import PrevSliderArrow            from '../../../components/icons/PrevSliderArrow';
import Meta                       from '../../../components/meta';
import PageHeader                 from '../../../components/pageHeader';
import { SelectInstallationType } from '../../../components/select-installation-type';
import { ShareBlock }             from '../../../components/share';
import { GallerySlider }          from '../../../components/sliders';
import TableSection               from '../../../components/table/TableSection';
import TableSeriesComparison      from '../../../components/table/TableSeriesComparison';
import { RenderTabsWithDropdown } from '../../../components/tabs/RenderTabsWithDropdown';
import useDataLayer               from '../../../hooks/data-layer';
import { OffersSlider, Price }    from '../../../new-components';

import { useCatalog }   from '../../../providers/catalog';
import catalogConstants from '../../../providers/catalog/catalog.constants';
import { useCommon }    from '../../../providers/common';
import { useModal }     from '../../../providers/modal';
import { useSidebar }   from '../../../providers/sidebar';
import sidebarConstants from '../../../providers/sidebar/sidebar.constants';
import { useTab }       from '../../../providers/tabs';
import { useUser }      from '../../../providers/user';

import cardMoreInfoPng         from '../../../assets/images/cardMoreInfo.png';
import interiorCardMoreInfoPng from '../../../assets/images/interiorcardMoreInfo.png';

import checkAccessOffer from '../../_common/utils/checkAccessOffer';

const Element = function() {
  const history  = useHistory();
  const location = useLocation();
  const match    = useRouteMatch();
  const { t }    = useTranslation();

  const catalog = useCatalog();
  const common  = useCommon();
  const modal   = useModal();
  const sidebar = useSidebar();
  const user    = useUser();

  const {
          registerTabsComponent,
          changeTab,
          ...tabs
        } = useTab();

  const prevSeriesId = usePrevious(catalog.element.currentSeriesId);
  const prevUrl      = usePrevious(match.url);

  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);

  const findInModels = React.useCallback((id) => {
    for (let modelId in catalog.element.models) {
      if (Object.hasOwn(catalog.element.models, modelId)) {
        let model = catalog.element.models[modelId];

        for (let seriesId in model) {
          if (Object.hasOwn(model, seriesId)) {
            let series = model[seriesId];

            for (let offerId in series) {
              if (Object.hasOwn(series, offerId)) {
                let offer = series[offerId];

                if (DV.isUndefined(id)) {
                  if (offer.common.urlKey === location.pathname) {
                    return offer;
                  }
                } else if (offerId.toString() === id.toString()) {
                  return offer;
                }
              }
            }
          }
        }
      }
    }

    return false;
  }, [catalog.element.models, location.pathname]);
  const changeOffer  = React.useCallback((offerId) => {
    const offer = findInModels(offerId);

    history.replace({
      pathname: offer.common.urlKey,
      state:    location.state,
    });
  }, [findInModels, location.state]);
  const onResize     = React.useCallback(() => {
    setIsMobile(window.screen.width <= 990);
  }, []);

  const offer = React.useMemo(() => {
    const tradeOffer = findInModels();

    if (!tradeOffer?.common) {
      return;
    }

    return tradeOffer;
  }, [catalog.element.currentSeriesId, findInModels, prevSeriesId, prevUrl, location.pathname, match.url]);

  const changeSeries         = React.useCallback((series) => {
    if (offer && series !== offer.common.seriesId && !DV.isNull(series)) {
      catalog.getOffersByModelAndSeries(
        offer.common.modelId,
        series,
        offer.common.offerGroupId,
        offer.common.isInterior,
      );
    }
  }, [offer]);
  const handleAddToBookmarks = React.useCallback(() => {
    if (offer) {
      user.addToBookmarks(offer.common);
    }
  }, [offer]);
  const seriesComparison     = React.useCallback(() => {
    if (offer) {
      modal.showModal(
        t('client_comparingSeries'),
        {
          component: TableSeriesComparison,
          props:     { series: catalog.element.allowedSeries[offer.common.modelId] },
        },
        true,
        'compare_modal',
      );
    }
  }, [catalog.element.allowedSeries, offer]);
  const showInfo             = React.useCallback((label, content) => {
    if (offer) {
      let data = content;

      try {
        data = JSON.parse(content);
      } catch (error) {
      }

      modal.showModal(
        label,
        data,
        false,
        classNames('show_info_modal', {
          with_table: typeof data === 'string' && !offer.common.isInterior,
        }),
      );
    }
  }, [offer?.common.isInterior]);

  const activeTab = tabs[catalogConstants.CATALOG_ELEMENT_TABS] || 0;

  const currentCity       = React.useMemo(() => {
    return user.cities?.find((city) => city.cityId === user.location.cityId);
  }, [user.cities, user.location.cityId]);
  // Value of a characteristic row of offer.sections (side for two-sided parameters).
  const sectionValue      = React.useCallback((sectionAlias, rowAlias, side = null) => {
    const row = offer?.sections
      ?.find((section) => section.alias === sectionAlias)
      ?.rows.find((item) => item.alias === rowAlias);

    return (side ? row?.value?.[side] : row?.value)?.value;
  }, [offer?.sections]);
  const header            = React.useMemo(() => {
    if (!offer) {
      return null;
    }

    return (
      <PageHeader
        className={'product_card_title'}
        headerClass={'product_card_header'}
        lockSystem={{ smartLock: { value: sectionValue('lock', 'lock@lp-smart') } }}
        schema
        sku={offer.common.vendorCode}
      >
        {t(offer.common.isInterior ? 'interior_door' : 'entrance_door')}{' '}
        {offer.common.label} <br />
      </PageHeader>
    );
  }, [offer, sectionValue]);
  const insidePanelColor  = React.useMemo(() => {
    return sectionValue('finishing', 'panelColor', 'inside');
  }, [sectionValue]);
  const isAvailable       = React.useMemo(() => {
    return checkAccessOffer(offer, currentCity);
  }, [currentCity, offer]);
  // Characteristic tabs come from the parameter sections configured in the admin panel (offer.sections).
  const kitSections       = React.useMemo(() => {
    return offer?.sections || [];
  }, [offer?.sections]);
  const moreInfo          = React.useMemo(() => {
    if (!offer) {
      return null;
    }

    return (
      <section className={'section_more_info'}>
        {offer.common.isMarker
          ? DV.isNotEmptyString(offer.common.moreInfo)
            ? (
              <div
                className={'moreInfo'}
                dangerouslySetInnerHTML={{ __html: offer.common.moreInfo }}
              />
            )
            : null
          : (
            <div className={'moreInfo base'}>
              <picture>
                <img
                  alt={t('label_card_more_info_alt')}
                  src={offer.common.isInterior ? interiorCardMoreInfoPng : cardMoreInfoPng}
                />
              </picture>
              <div>
                <div className={'title'}>{t('label_card_more_info_title')}</div>
                <p>{t(`label_card_more_info_description${offer.common.isInterior ? '_interior' : ''}`)}</p>
                <RenderButton
                  label={t('btn_more_info')}
                  target={'_blank'}
                  type={'link'}
                  uri={offer.common.isInterior
                    ? common.getUrlByTemplate('about-interior-doors')
                    : offer.common.moreInfoLink
                  }
                />
              </div>
            </div>
          )
        }
      </section>
    );
  }, [offer?.common]);
  const nav               = React.useMemo(() => {
    return kitSections.map((section) => ({ label: section.label }));
  }, [kitSections]);
  const outsidePanelColor = React.useMemo(() => {
    return sectionValue('finishing', 'panelColor', 'outside');
  }, [sectionValue]);
  const promotionTag      = React.useMemo(() => {
    return offer?.common?.tags?.find((tag) => {
      return tag.aliasKey === 'akciya';
    });
  }, [offer?.common?.tags]);
  const tools_btn         = React.useMemo(() => {
    if (!offer) {
      return null;
    }

    return (
      <div className={'tools_btn'}>
        <RenderButton
          action={handleAddToBookmarks}
          className={'like_btn'}
          icon={
            <span
              className={
                !!user.bookmarks?.[offer.common.modelId]?.[`_${offer.common.offerId}`]
                  ? 'ic-like-fill'
                  : 'ic-like'
              }
            />
          }
          key={'bookmark'}
        />
        {isAvailable && <ShareBlock key={'share'} />}
        <CompareButton
          isInterior={offer.common.isInterior}
          key={'compare'}
          offer={offer.common}
        />
      </div>
    );
  }, [handleAddToBookmarks, offer?.common, user.bookmarks]);
  const visibleTags       = React.useMemo(() => {
    const sortedTags = offer?.common?.tags.sort((a, b) => b.priority - a.priority) ?? [];

    // Show only two tags
    if (promotionTag) {
      const otherTags  = sortedTags.filter((tag) => {
        return tag !== promotionTag;
      }) ?? [];
      const [topOther] = otherTags;

      return topOther ? [promotionTag, topOther] : [promotionTag];
    }

    return sortedTags.slice(0, 2);
  }, [offer?.common?.tags, promotionTag]);

  useDataLayer(offer);

  React.useEffect(() => {
    if (DV.isNull(activeTab)) {
      registerTabsComponent(catalogConstants.CATALOG_ELEMENT_TABS);
    }

    scrollTo({
      left: 0,
      top:  0,
    });

    if (!catalog.loading && !isLoaded) {
      if (!offer) {
        catalog.getCatalogItem(match.url)
          .then(() => {
            setIsLoaded(true);
          })
          .catch(() => {
          });
      }
    }

    onResize();

    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  React.useEffect(() => {
    if (
      !DV.isNull(catalog.element.currentSeriesId) &&
      !DV.isNull(prevSeriesId) &&
      catalog.element.currentSeriesId !== prevSeriesId &&
      !DV.isUndefined(catalog.element.models?.[catalog.element.currentModelId]?.[catalog.element.currentSeriesId]?.[catalog.element.currentOfferId])
    ) {
      history.replace({
        pathname: catalog.element.models[catalog.element.currentModelId][catalog.element.currentSeriesId][catalog.element.currentOfferId].common.urlKey,
        state:    location.state,
      });
    } else {
      if (offer) {
        catalog.getCatalogItem(match.url);
      } else if (match.url !== prevUrl) {
        catalog.getCatalogItem(match.url);

        scrollTo({
          left: 0,
          top:  0,
        });
      }
    }
  }, [catalog.element.currentSeriesId, prevSeriesId, match.url]);

  return (
    <>
      <div className={'page-content'}>
        {offer && (
          <>
            <Meta {...offer.common.meta} />
            <main className={classNames('product', { interior: offer.common.isInterior })}>
              <section
                className={classNames('card', {
                  double:   offer.common.constructionTypeId === 2 || offer.common.singleSideView,
                  interior: offer.common.isInterior,
                })}
              >
                <div className={'container'}>
                  <div className={'wrapper'}>
                    {isMobile && (
                      <>
                        <SelectInstallationType offer={offer} />
                        {header}
                        {tools_btn}
                      </>
                    )}
                    <OffersSlider
                      current={offer.common.offerId}
                      isDouble={offer.common.constructionTypeId === 2 || offer.common.singleSideView}
                      magnifier
                      offers={catalog.element.models[offer.common.modelId][offer.common.seriesId]}
                      onChange={changeOffer}
                      schema
                      showOffers
                    />
                    <div className={'under_photo_container'}>
                      {offer.common.mark && <small className={'bottom_mark'}>{offer.common.mark}</small>}
                      <small className={'bottom_mark'}>{t('label_mark_under_the_photo_example')}</small>
                    </div>
                    {!offer.common.isInterior && <>{moreInfo}</>}
                  </div>
                  <div className={'description'}>
                    {!isMobile && (
                      <>
                        <SelectInstallationType offer={offer} />
                        {header}
                      </>
                    )}
                    {DV.isNotEmptyArray(visibleTags) && (
                      <div className={'tags'}>
                        {visibleTags.map((tag) => {
                          const style = {
                            backgroundColor: tag.isColorEnabled ? `${tag.tagColor}99` : null,
                            border:          tag.isColorEnabled ? `1.5px solid ${tag.tagColor}` : null,
                            color:           tag.isColorEnabled ? tag.tagTextColor : null,
                          };

                          if (DV.isNotEmptyString(tag.link)) {
                            return (
                              <Link
                                className={'tag'}
                                key={tag.tagId}
                                style={style}
                                target={'_blank'}
                                to={tag.link}
                              >{tag.label}</Link>
                            );
                          }

                          return (
                            <span
                              className={'tag'}
                              key={tag.tagId}
                              style={style}
                            >{tag.label}</span>
                          );
                        })}
                      </div>
                    )}
                    <div
                      className={'body'}
                      dangerouslySetInnerHTML={{ __html: offer.common.description }}
                    />
                    {offer.common.isInterior && (
                      <div className={'door_specification_wrapper'}>
                        {!!sectionValue('interiorCloth', 'interiorMaterial') && (
                          <p>
                            <span className={'specification_title'}>{t('field_interiorMaterialLabel')}: </span>
                            <span className={'specification_description'}>
                              {sectionValue('interiorCloth', 'interiorMaterial')}
                            </span>
                          </p>
                        )}
                        {!!sectionValue('interiorCloth', 'interiorPanelColor') && (
                          <p>
                            <span className={'specification_title'}>{t('field_interiorPanelColorLabel')}: </span>
                            <span className={'specification_description'}>
                              {sectionValue('interiorCloth', 'interiorPanelColor')}
                            </span>
                          </p>
                        )}
                      </div>
                    )}
                    {(
                      !!insidePanelColor ||
                      !!outsidePanelColor ||
                      !!offer.common.insertColorInside ||
                      !!offer.common.insertColorOutside
                    ) && (
                      <div className={'finishing'}>
                        {!!outsidePanelColor && (
                          <div>
                            <span>{t('label_outside')}: </span>
                            {outsidePanelColor}
                          </div>
                        )}
                        {!!offer.common.insertColorOutside && (
                          <div>
                            <span>{t('label_inserts_outside')}: </span>
                            {offer.common.insertColorOutside}
                          </div>
                        )}
                        {!!insidePanelColor && (
                          <div>
                            <span>{t('label_inside')}: </span>
                            {insidePanelColor}
                          </div>
                        )}
                        {!!offer.common.insertColorInside && (
                          <div>
                            <span>{t('label_inserts_inside')}: </span>
                            {offer.common.insertColorInside}
                          </div>
                        )}
                      </div>
                    )}
                    <div className={'pay'}>
                      {isAvailable
                        ? !offer.common.isInterior
                          ? (
                            <Price
                              discountPercent={offer.common.discountPercent}
                              discountPrice={offer.common.discountPrice}
                              help={(
                                <div>
                                  <p dangerouslySetInnerHTML={{ __html: t('label_mark_price_info') }} />
                                </div>
                              )}
                              isExpressMarker={offer.common.isExpressMarker}
                              isMarker={offer.common.isMarker}
                              isSpecMarker={offer.common.isSpecMarker}
                              price={offer.common.price}
                              priceValidUntil={offer.common.updatedAt}
                              schema
                              showDiscountPrice={offer.common.showDiscountPrice}
                            />
                          )
                          : null
                        : (
                          <p>
                            <strong>{t('client_modelNotAvailable')}</strong>
                          </p>
                        )
                      }
                      <div className={'compare_wrapper'}>
                        {Object.keys(catalog.element.allowedSeries[offer.common.modelId]).length > 1 && (
                          <>
                            <Dropdown
                              labelKey={'label'}
                              onChangeCallback={changeSeries}
                              options={Object.values(catalog.element.allowedSeries[offer.common.modelId] || {})
                                .sort((a, b) => a.priority > b.priority ? 1 : -1)
                              }
                              selected={offer.common.seriesId}
                              valueKey={'seriesId'}
                            />
                            <RenderButton
                              action={seriesComparison}
                              className={'primary'}
                              label={t('btn_compare')}
                            />
                          </>
                        )}
                      </div>
                      <div className={'action_buttons'}>
                        <RenderButton
                          action={() => sidebar.showSidebar(sidebarConstants.CALL_ME_SIDEBAR)}
                          label={t('btn_get_advice')}
                        />
                        {!isMobile ? tools_btn : null}
                      </div>
                    </div>
                    {offer.common.isInterior && <>{moreInfo}</>}
                  </div>
                </div>
              </section>
              {DV.isArray(offer.common.gallery) && (
                <GallerySlider
                  dots={false}
                  nextArrow={<NextSliderArrow />}
                  prevArrow={<PrevSliderArrow />}
                  previews
                  previewsSize={150}
                  showTitle
                  slides={offer.common.gallery.map((item) => {
                    return {
                      alt:         item.alt || item.description,
                      description: item.description,
                      priority:    item.priority,
                      src:         item.src,
                      type:        item.type,
                      url:         item.url,
                    };
                  })}
                />
              )}
              <section className={'kit'}>
                <div className={'container'}>
                  <RenderTabsWithDropdown
                    active={activeTab}
                    changeTab={(index) => changeTab(catalogConstants.CATALOG_ELEMENT_TABS, index)}
                    dropdown={{ options: [] }}
                    isCollapsed
                    nav={nav}
                  >
                    {DV.isNotEmptyArray(kitSections) && kitSections.map((section) => (
                      <React.Fragment key={section.alias}>
                        <TableSection
                          section={section}
                          showInfo={showInfo}
                        />
                        <CatalogMoreInfo moreInfo={section.moreInfo} />
                      </React.Fragment>
                    ))}
                  </RenderTabsWithDropdown>
                </div>
              </section>
              {DV.isNotEmptyArray(catalog.element.popularModels) && (
                <section className={'popular'}>
                  <PopularSlider
                    installationTypeId={offer.common.installationTypeId}
                    isInterior={offer.common.isInterior}
                    popularModels={catalog.element.popularModels}
                  />
                </section>
              )}
            </main>
          </>
        )}
        <ConsultationBlock maxWidth={'1212px'} />
      </div>
      {(user.bookmarksLoading || catalog.loading) && <Preloader />}
    </>
  );
};

export default Element;
